/**
 * @file: useValidateFacadeSiteRequirements.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { isMediaStagedPending } from '@euler/app/flows/task/functions/task-detail/util';
import {
  facadeSiteHasMedia,
  facadeSiteRequirementsOf,
  resolveFacadeSites,
} from '@euler/functions/inventory/facadeSiteRequirements';
import { useAlert } from '@euler/functions/ux/useAlert';
import { SiteInspectionType } from '@euler/model/enum';
import { SiteInspectionStaged } from '@euler/model/task-detail';
import { array2map } from '@euler/utils/array';
import { usePersistFn } from '@euler/utils/hooks';
import { useMemo } from 'react';

// only an uploaded media survives into the create-order payload - a failed or
// in-flight one is dropped by `mediaStagedToCommitPayload`, so a site that only
// holds those would reach the customer with no photo at all.
const hasUploadedMedia = (siteInspection: SiteInspectionStaged | undefined) =>
  Boolean(
    siteInspection?.items.some(x =>
      x.medias.some(media => !isMediaStagedPending(media)),
    ),
  );

/**
 * Gates pushing the pre-inspection report from the order flow: the facade
 * template's required sites have to be photographed, and those photos have to
 * have landed. Alerts and answers false when they have not.
 */
export const useValidateFacadeSiteRequirements = () => {
  const { facadeInspectionTemplate, orderDetail$, inventoryQuery } =
    useOrderFlowContext();

  const alert = useAlert();

  const { requiredSiteIds } = useMemo(
    () => facadeSiteRequirementsOf(facadeInspectionTemplate?.conf),
    [facadeInspectionTemplate],
  );

  return usePersistFn(() => {
    if (!requiredSiteIds.length) return true;

    const siteInspections = array2map(
      orderDetail$
        .getValue()
        .preInspection?.filter(
          x => x.inspectionType === SiteInspectionType.Facade,
        ) ?? [],
      x => x.siteId,
    );

    const missingSiteIds: number[] = [];
    const pendingSiteIds: number[] = [];

    for (const siteId of requiredSiteIds) {
      const siteInspection = siteInspections.get(siteId);
      if (!facadeSiteHasMedia(siteInspection)) {
        missingSiteIds.push(siteId);
      } else if (!hasUploadedMedia(siteInspection)) {
        pendingSiteIds.push(siteId);
      }
    }

    const names = (siteIds: number[]) =>
      resolveFacadeSites(siteIds, inventoryQuery.getSiteById)
        .map(x => x.name)
        .join('、');

    // a site that was shot but whose upload has not landed reports itself,
    // rather than reading as one that was never photographed.
    if (pendingSiteIds.length) {
      void alert({
        title: '提示',
        message: `以下必检部位的照片尚未上传成功，请稍候或重新上传后再推送预检报告: ${names(
          pendingSiteIds,
        )}`,
      });
      return false;
    }

    if (!missingSiteIds.length) return true;

    void alert({
      title: '提示',
      message: `以下必检部位尚未拍照记录，请完成后再推送预检报告: ${names(
        missingSiteIds,
      )}`,
    });
    return false;
  });
};
